import { Injectable } from '@angular/core'
import { Language, Languages } from '@core/model/enums/language.enum'
import { LanguageService } from '@core/service/language.service'
import { forkJoin, map, of, switchMap } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { OutputField } from '@app/features/scope-overview/model/output-field.model'
import { plainToInstance } from 'class-transformer'
import { asMap } from '@shared/utils/utils'
import { OutputTemplate } from '@app/features/scope-overview/model/output-template.model'

declare const CKEDITOR: any

export interface OutputThemeDefinition {
  name: string
  title: string
  description?: string
  languages: Language[]
  applicationSetting?: string
  thumbnailUri?: string
  baseUri?: string
  blocks?: any[]
  companyType: string[]
  pageBreakable?: boolean
  selectedLanguage?: Language
}

@Injectable({ providedIn: 'root' })
export class OutputThemeService {
  scopeTemplates: Record<string, OutputThemeDefinition>
  sowTemplates: Record<string, OutputThemeDefinition>

  constructor(
    private lang: LanguageService,
    private http: HttpClient
  ) {
    this.scopeTemplates = {
      BLANK: {
        name: 'BLANK',
        title: 'Blank Template',
        languages: [Languages.EN, Languages.FR, Languages.DE, Languages.PR, Languages.ES],
        applicationSetting: 'SCOPE_OUTPUT_DOCX__BLANK_TEMPLATE',
        companyType: ['AGENCY', 'CLIENT', 'LEGAL', 'COMPANY', 'TBMC'],
      },
      ENGAGEMENT_LETTER: {
        name: 'ENGAGEMENT_LETTER',
        title: 'Engagement Letter',
        thumbnailUri: 'standard-template.png',
        companyType: ['LEGAL'],
        languages: [Languages.EN],
        baseUri: 'assets/output-templates/scope/engagement-letter',
        blocks: [
          { name: 'block.cover.page', fieldType: 'COVER_PAGE' },
          { name: 'block.header', fieldType: 'BLANK_HEADER' },
          { name: 'block.introduction', template: '/html/01-introduction.html' },
          { name: 'block.initial.matter', template: '/html/02-initial-matter.html' },
          { name: 'block.scope.of.legal.services', template: '/html/03-scope-of-legal-services.html' },
          { name: 'block.concerns', template: '/html/04-concerns.html' },
          { name: 'block.signature.and.terms', template: '/html/05-signature-and-terms.html' },
          { name: 'block.footer', fieldType: 'BLANK_FOOTER' },
        ],
      },
      STANDARD_TEMPLATE: {
        name: 'STANDARD_TEMPLATE',
        title: `${this.lang.get('scope')} ${this.lang.get('output')} Template`,
        thumbnailUri: 'standard-template.png',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY', 'TBMC'],
        languages: [Languages.EN, Languages.FR, Languages.DE, Languages.PR, Languages.ES],
        baseUri: 'assets/output-templates/scope/standard-template',
        blocks: [
          { name: 'block.cover.page', fieldType: 'COVER_PAGE' },
          { name: 'block.header', fieldType: 'BLANK_HEADER' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
      BASIC_QUOTE: {
        name: 'BASIC_QUOTE',
        title: `Basic Quote Template`,
        description: `Editable template with cover page and footer`,
        thumbnailUri: 'basic-quote-preview.jpg',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY', 'TBMC'],
        pageBreakable: false,
        languages: [Languages.EN],
        baseUri: 'assets/output-templates/scope/basic-quote',
        blocks: [
          { name: 'block.cover.page', template: '/html/cover.html', fieldType: 'COVER_PAGE' },
          { name: 'block.header', fieldType: 'BLANK_HEADER' },
          { name: 'block.main.section', template: '/html/01-main.html' },
          { name: 'block.pricing.table', template: '/html/03-staffing.html' },
          { name: 'block.staffing.plan', template: '/html/02-pricing-table.html' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
      SCOPE_QUOTE: {
        name: 'SCOPE_QUOTE',
        title: `Quotation for scope with stages`,
        description: `Editable template. Pre-requisite: all deliverables must be placed in scope stages (sections).`,
        thumbnailUri: 'scope-quotation-preview.png',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY', 'TBMC'],
        pageBreakable: false,
        languages: [Languages.EN],
        baseUri: 'assets/output-templates/scope/scope-quote',
        blocks: [
          { name: 'block.header', template: '/html/header.html', fieldType: 'BLANK_HEADER' },
          { name: 'block.main.section', template: '/html/01-main.html' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
      AICE_TEMPLATE: {
        name: 'AICE_TEMPLATE',
        title: `AICE Template`,
        description: `Editable template.`,
        thumbnailUri: 'aice-template.png',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY', 'TBMC'],
        pageBreakable: false,
        languages: [Languages.EN],
        baseUri: 'assets/output-templates/scope/aice-template',
        blocks: [
          { name: 'block.header', template: '/html/header.html', fieldType: 'BLANK_HEADER' },
          { name: 'block.main.section', template: '/html/01-main.html' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
      CHANGE_REQUEST_TEMPLATE: {
        name: 'CHANGE_REQUEST_TEMPLATE',
        title: 'Change Request Template',
        thumbnailUri: 'change-request-template.png',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY', 'TBMC'],
        pageBreakable: false,
        languages: [Languages.EN],
        baseUri: 'assets/output-templates/scope/change-request-template',
        blocks: [
          { name: 'block.cover.page', fieldType: 'COVER_PAGE' },
          { name: 'block.header', template: '/html/header.html', fieldType: 'BLANK_HEADER' },
          { name: 'block.summary', template: '/html/01-summary.html' },
          { name: 'block.deliverables', template: '/html/02-deliverables.html' },
          { name: 'block.terms', template: '/html/03-terms.html' },
          { name: 'block.signatures', template: '/html/04-signatures.html' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
    }

    this.sowTemplates = {
      BLANK: {
        name: 'BLANK',
        title: `${this.lang.get('sow')} Blank Template`,
        languages: [Languages.EN, Languages.FR, Languages.DE, Languages.PR, Languages.ES],
        companyType: ['AGENCY', 'CLIENT', 'LEGAL', 'COMPANY'],
      },
      STANDARD_TEMPLATE: {
        name: 'STANDARD_TEMPLATE',
        title: `${this.lang.get('sow')} ${this.lang.get('output')} Template`,
        thumbnailUri: 'standard-template.png',
        companyType: ['AGENCY', 'CLIENT', 'COMPANY'],
        languages: [Languages.EN, Languages.FR, Languages.DE, Languages.PR, Languages.ES],
        baseUri: 'assets/output-templates/sow/standard-template',
        blocks: [
          { name: 'block.cover.page', fieldType: 'COVER_PAGE' },
          { name: 'block.header', fieldType: 'BLANK_HEADER' },
          { name: 'block.summary', template: '/html/01.html' },
          { name: 'block.exe-summary', template: '/html/02.html' },
          { name: 'block.background', template: '/html/03.html' },
          { name: 'block.terms', template: '/html/04.html' },
          { name: 'block.footer', template: '/html/footer.html', fieldType: 'BLANK_FOOTER' },
        ],
      },
    }
  }

  getFromTemplate(templateScopeType: boolean, documentTemplate: string) {
    return (
      (templateScopeType ? this.scopeTemplates[documentTemplate]?.title : this.sowTemplates[documentTemplate]?.title) ||
      'From Deleted Template'
    )
  }

  composeSections(template: OutputTemplate, documentTemplate: OutputThemeDefinition) {
    if (!documentTemplate.baseUri) {
      return of(null)
    } else {
      return this.fetchLanguageProps(documentTemplate.baseUri, template.language).pipe(
        switchMap((langProps: any) => {
          const promises = []

          documentTemplate.blocks.forEach((block, index) => {
            const field = plainToInstance(OutputField, {
              name: langProps.get(block.name) || block.name,
              fieldType: block.fieldType || 'CONTENT_BLOCK',
              order: index + 1,
            })
            template.outputFields.push(field)
            if (block.template) {
              promises.push(this.applyTemplateContent(documentTemplate.baseUri + block.template, field, langProps))
            } else {
              field.content = ''
            }
          })
          return forkJoin(promises)
        })
      )
    }
  }

  private fetchLanguageProps(baseUri, language) {
    return this.http.get(`${baseUri}/lang/${language.toLowerCase()}.properties`, { responseType: 'json' }).pipe(
      map((langProps: string) => {
        return {
          langCode: language.toLowerCase(),
          props: langProps,
          get: (key) => {
            return langProps[key]
          },
        }
      })
    )
  }

  private applyTemplateContent(templateUri, outputField, langProps) {
    return this.http.get(templateUri, { responseType: 'text' }).pipe(
      switchMap((html: string) => {
        const tmp = $('<div></div>').html(html)
        this.initLangs(tmp, langProps)
        return this.initWidgets(tmp, langProps.langCode).pipe(
          map(() => {
            outputField.content = tmp.html()
          })
        )
      })
    )
  }

  private initLangs(tmp, langProps) {
    tmp.find('[lang-key]').each((i, el) => {
      const filters = el
        .getAttribute('lang-key')
        .split('|')
        .map((k) => k.trim())
      const key = filters[0]
      let value = langProps.get(key)
      if (value == null) {
        value = '??' + el.innerText + '??'
      }
      if (filters.length > 1) {
        const f1 = filters[1]
        if (f1 == 'uppercase') {
          value = value.toUpperCase()
        }
      }

      el.innerText = value
    })
  }

  private initWidgets(tmp, langCode) {
    const uninitialisedWidgets = tmp.find('[widget-init-required]')
    if (uninitialisedWidgets.length > 0) {
      const keys = Object.keys(asMap(uninitialisedWidgets.toArray(), 'tagName', (i) => i.toLowerCase()))
      const promises = []

      keys.forEach((tag) => {
        promises.push(
          CKEDITOR.plugins.load(tag, (plugins: any[]) => {
            const resourceHtml = plugins[tag].createWidgetTemplate(langCode)
            const els = tmp.find(tag)
            els.replaceWith(resourceHtml)
          })
        )
      })
      return forkJoin(keys)
    }
    return of([])
  }
}
